import { useMemo } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Card, Typography, Chip, Pagination } from '@mui/material';
import { 
  DataGridPremium, 
  useGridApiRef,
  GridToolbar,
  GridToolbarColumnsButton,
  useGridSelector,
  gridPageCountSelector,
  gridPageSelector,
  type GridColDef, 
  type GridValidRowModel,
  type GridRowIdGetter,
  type GridRenderCellParams,
  type GridApiPremium,
} from '@mui/x-data-grid-premium';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import type { CustomPaginationProperties, DataGridColumn, DataGridProperties } from '@/types/application/common/components/tables/data-grid';
import type { RefObject } from 'react';

export type { DataGridColumn } from '@/types/application/common/components/tables/data-grid';

// Custom Pagination Component
function CustomPagination({ apiRef }: CustomPaginationProperties) {
  const page = useGridSelector(apiRef as RefObject<GridApiPremium>, gridPageSelector);
  const pageCount = useGridSelector(apiRef as RefObject<GridApiPremium>, gridPageCountSelector);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5 }}>
      <Pagination
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current?.setPage(value - 1)}
        color="primary"
        shape="rounded"
        showFirstButton
        showLastButton
        siblingCount={1}
        boundaryCount={1}
      />
    </Box>
  );
}

export function DataGrid<T extends GridValidRowModel = GridValidRowModel>({
  title,
  columns,
  data,
  emptyMessage = 'No data',
  emptyIcon,
  getRowId,
  getRowClassName,
  gridSx,
  loading = false,
  autoHeight = true,
  rowHeight = 52,
  showToolbar = true,
  headerFilters = false,
  showToolbarFilterPanelButton = true,
  showToolbarColumnsButton = true,
  showToolbarExport = true,
  disableDensitySelector = true,
  initialPageSize = 10,
  disableRowSelectionOnClick = false,
}: DataGridProperties<T>) {
  const apiRef = useGridApiRef();

  const toolbarSlotProps = useMemo(
    () => ({
      showQuickFilter: true as const,
      quickFilterProps: { debounceMs: 300 },
      ...(!showToolbarExport
        ? {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          }
        : {}),
      ...(!showToolbarFilterPanelButton
        ? { mainControls: <GridToolbarColumnsButton /> }
        : {}),
    }),
    [showToolbarExport, showToolbarFilterPanelButton],
  );

  // Convert our column format to GridColDef
  const gridColumns: GridColDef[] = useMemo(() => {
    // Calculate optimal column width based on content if not specified
    const calculateColumnWidth = (col: DataGridColumn<T>): number => {
      if (col.width) return col.width;
      
      // Estimate width based on header label length
      let estimatedWidth = col.label.length * 9 + 50; // ~9px per character + padding
      
      // Estimate width based on sample data content
      if (data.length > 0) {
        const sampleValues = data.slice(0, Math.min(20, data.length)).map(row => {
          if (col.render) {
            const rendered = col.render(row);
            // Estimate rendered content length
            const renderedStr = String(rendered);
            // Account for React elements (chips, tooltips, etc.) - estimate larger
            if (renderedStr.includes('[object Object]') || renderedStr.length < 5) {
              return 80; // Default for rendered components
            }
            return renderedStr.length;
          }
          const value = (row as Record<string, unknown>)[col.key];
          return String(value ?? '').length;
        });
        const maxContentLength = Math.max(
          ...sampleValues,
          col.label.length
        );
        // Calculate width: ~8px per character + extra padding for icons/chips
        estimatedWidth = maxContentLength * 8 + 80;
      }
      
      // Ensure minimum width
      return Math.max(estimatedWidth, col.minWidth ?? 120);
    };

    return columns.map((col) => {
      const calculatedWidth = calculateColumnWidth(col);
      return {
        field: col.key,
        headerName: col.label,
        width: col.width ?? calculatedWidth,
        minWidth: col.minWidth ?? 100,
        flex: col.flex ?? (col.width ? undefined : undefined), // No flex for auto-sizing
        hideable: col.hideable ?? true,
        renderCell: col.render 
          ? (params: GridRenderCellParams) => col.render!(params.row as T)
          : undefined,
      };
    });
  }, [columns, data]);

  // Default getRowId function
  const defaultGetRowId: GridRowIdGetter<T> = (row: T) => {
    const r = row as Record<string, unknown>;
    return (r.sysId || r.sys_id || r.transactionId || r.id || r.orgId || r.cronName || Math.random().toString()) as string;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        px: 2.5, 
        py: 2, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6">{title}</Typography>
        <Chip label={data.length} size="small" variant="outlined" />
      </Box>

      {/* Empty State */}
      {data.length === 0 && !loading ? (
        <Box sx={{ py: 6, textAlign: 'center', flex: 1 }}>
          {emptyIcon || <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 300 }}>
          <DataGridPremium
            apiRef={apiRef}
            rows={data}
            columns={gridColumns}
            loading={loading}
            getRowId={getRowId || defaultGetRowId}
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            autoHeight={autoHeight}
            rowHeight={rowHeight}
            showToolbar={showToolbar}
            headerFilters={headerFilters}
            disableColumnSelector={!showToolbarColumnsButton}
            disableDensitySelector={disableDensitySelector}
            columnHeaderHeight={48}
            pagination
            pageSizeOptions={[5, 10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: initialPageSize, page: 0 },
              },
            }}
            disableMultipleRowSelection
            slots={{
              toolbar: GridToolbar,
              pagination: () => <CustomPagination apiRef={apiRef} />,
            }}
            slotProps={{
              toolbar: toolbarSlotProps,
            }}
            sx={[
              {
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
                overflow: 'visible',
                textOverflow: 'clip',
                whiteSpace: 'nowrap',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                borderColor: 'divider',
                '& .MuiTypography-root': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
              '& .MuiDataGrid-cellContent': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider',
                minHeight: 56,
              },
              '& .MuiDataGrid-toolbarContainer': {
                p: 1.5,
                gap: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              },
            },
              ...(gridSx ? [gridSx] : []),
            ] as SxProps<Theme>}
          />
        </Box>
      )}
    </Card>
  );
}

export default DataGrid;

